import { HttpHeaders } from '@angular/common/http';
import { ApolloLink } from '@apollo/client/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AccessTokenService } from './http/access-token.service';

export function createAuthLink(accessTokenService: AccessTokenService): ApolloLink {
    return new ApolloLink((operation, forward) => {
        const requireAuth = operation.getContext()['requireAuth'] ?? true;

        // Always try to get token - required or optional
        // prepareAccessToken handles both cases:
        // - requireAuth=true: throws if no token
        // - requireAuth=false: returns token if available, null otherwise
        return accessTokenService.prepareAccessToken(requireAuth).pipe(
            switchMap((token) => {
                operation.setContext((context: Partial<ApolloLink.OperationContext>) => {
                    let { headers } = context;

                    if (token) {
                        if (!headers) {
                            headers = new HttpHeaders();
                        }

                        headers = headers.append('Authorization', `Bearer ${token}`);
                    }

                    return ({
                        ...context,
                        headers
                    });
                });

                const signal = (operation.getContext()['fetchOptions'] as RequestInit | undefined)?.signal;

                if (!signal) {
                    return forward(operation);
                }

                return new Observable<ApolloLink.Result>((observer) => {
                    if (signal.aborted) {
                        observer.error(new DOMException('Aborted', 'AbortError'));

                        return;
                    }

                    const subscription = forward(operation).subscribe({
                        next: value => observer.next(value),
                        error: (error: unknown) => observer.error(error),
                        complete: () => observer.complete()
                    });
                    const onAbort = (): void => {
                        subscription.unsubscribe();
                        observer.error(new DOMException('Aborted', 'AbortError'));
                    };

                    signal.addEventListener('abort', onAbort, { once: true });

                    return () => {
                        signal.removeEventListener('abort', onAbort);
                        subscription.unsubscribe();
                    };
                });
            }));
    });
}
